"use client";

import { Link } from "react-router-dom";
import { TrendingUp, Hash, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/utils";

interface Tag {
  name: string;
  displayName: string;
  postCount: number;
  color: string;
}

interface TopContributor {
  uid: string;
  username: string;
  displayName: string;
  avatarUrl: string;
  reputation: number;
}

interface SidebarProps {
  popularTags?: Tag[];
  topContributors?: TopContributor[];
  stats?: {
    posts: number;
    users: number;
    comments: number;
  };
}

export function Sidebar({ popularTags = [], topContributors = [], stats }: SidebarProps) {
  return (
    <aside className="space-y-6">
      {/* Popular Tags */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-base">
            <Hash className="mr-2 h-4 w-4 text-indigo-600" />
            Popular Tags
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-2">
            {popularTags.length > 0 ? (
              popularTags.map((tag) => (
                <Link key={tag.name} to={`/tag/${tag.name}`}>
                  <Badge
                    variant="secondary"
                    className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                    style={{ borderLeft: `3px solid ${tag.color}` }}
                  >
                    #{tag.displayName}
                  </Badge>
                </Link>
              ))
            ) : (
              <>
                <Link to="/tag/react-native"><Badge variant="secondary">#react-native</Badge></Link>
                <Link to="/tag/expo"><Badge variant="secondary">#expo</Badge></Link>
                <Link to="/tag/typescript"><Badge variant="secondary">#typescript</Badge></Link>
                <Link to="/tag/navigation"><Badge variant="secondary">#navigation</Badge></Link>
                <Link to="/tag/performance"><Badge variant="secondary">#performance</Badge></Link>
                <Link to="/tag/animations"><Badge variant="secondary">#animations</Badge></Link>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Contributors - only show when there are contributors */}
      {topContributors.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-base">
              <TrendingUp className="mr-2 h-4 w-4 text-indigo-600" />
              Top Contributors
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {topContributors.map((contributor) => (
                <Link
                  key={contributor.uid}
                  to={`/user/${contributor.username}`}
                  className="flex items-center space-x-3 rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={contributor.avatarUrl} alt={contributor.displayName} />
                    <AvatarFallback>{getInitials(contributor.displayName)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate dark:text-white">
                      {contributor.displayName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {contributor.reputation} reputation
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Community Stats */}
      {stats && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-base">
              <Users className="mr-2 h-4 w-4 text-indigo-600" />
              Community Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-indigo-600">{stats.posts}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Posts</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-600">{stats.users}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Members</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-600">{stats.comments}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Comments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </aside>
  );
}

